import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import NotificationPanel from '../../components/ui/NotificationPanel';
import WarrantyTimelineChart from './components/WarrantyTimelineChart';
import WarrantyDataGrid from './components/WarrantyDataGrid';
import AlertManagementPanel from './components/AlertManagementPanel';
import FinancialAnalysisPanel from './components/FinancialAnalysisPanel';
import QuickActionToolbar from './components/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';


const WarrantyManagementCenter = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'timeline', label: 'Timeline', icon: 'Calendar' },
    { id: 'alerts', label: 'Alerts', icon: 'Bell' },
    { id: 'financial', label: 'Financial', icon: 'DollarSign' },
    { id: 'actions', label: 'Quick Actions', icon: 'Zap' }
  ];

  const integrationStatus = [
    { system: 'Procurement System', status: 'connected', lastSync: '2 minutes ago' },
    { system: 'Vendor Database', status: 'connected', lastSync: '5 minutes ago' },
    { system: 'Asset Management', status: 'connected', lastSync: '1 minute ago' },
    { system: 'Financial System', status: 'warning', lastSync: '15 minutes ago' }
  ];

  useEffect(() => {
    // Simulate real-time updates
    const interval = setInterval(() => {
      // Update integration status timestamps
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setRefreshing(false);
  };

  const handleBulkAction = (action, data) => {
    console.log('Bulk action:', action, data);
    // Handle bulk actions like renewal, quote requests, etc.
  };

  const handleQuickAction = (action, data) => {
    console.log('Quick action:', action, data);
    // Handle quick actions from toolbar
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-success bg-success/10';
      case 'warning':
        return 'text-warning bg-warning/10';
      case 'error':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <WarrantyTimelineChart />
            <WarrantyDataGrid
              onAssetSelect={setSelectedAssets}
              selectedAssets={selectedAssets}
              onBulkAction={handleBulkAction}
            />
          </div>
        );
      case 'timeline':
        return <WarrantyTimelineChart />;
      case 'alerts':
        return <AlertManagementPanel />;
      case 'financial':
        return <FinancialAnalysisPanel />;
      case 'actions':
        return (
          <QuickActionToolbar
            selectedAssets={selectedAssets}
            onAction={handleQuickAction}
          />
        );
      default:
        return (
          <div className="space-y-6">
            <WarrantyTimelineChart />
            <WarrantyDataGrid
              onAssetSelect={setSelectedAssets}
              selectedAssets={selectedAssets}
              onBulkAction={handleBulkAction}
            />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="p-6">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div className="mb-4 lg:mb-0">
              <h1 className="text-3xl font-bold text-foreground mb-2">Warranty Management Center</h1>
              <p className="text-muted-foreground">
                Proactive warranty tracking and renewal management for optimal procurement planning
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={handleRefresh}
                loading={refreshing}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh Data
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowNotifications(!showNotifications)}
                iconName="Bell"
                iconPosition="left"
              >
                Notifications
              </Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
              >
                Export Report
              </Button>
            </div>
          </div>

          {/* Integration Status Bar */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Wifi" size={20} className="text-success" />
                <span className="font-medium text-card-foreground">System Integration Status</span>
              </div>
              <div className="flex items-center space-x-6">
                {integrationStatus?.map((system, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${
                      system?.status === 'connected' ? 'bg-success' :
                      system?.status === 'warning' ? 'bg-warning' : 'bg-error'
                    }`} />
                    <span className="text-sm text-muted-foreground">{system?.system}</span>
                    <span className="text-xs text-muted-foreground">({system?.lastSync})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-card border border-border rounded-lg mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-6">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {renderTabContent()}
          </div>

          {/* Footer */}
          <footer className="mt-12 py-8 border-t border-border">
            <div className="bg-accent/5 border border-accent/20 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Info" size={24} className="text-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-card-foreground mb-2">Warranty Management Guidelines</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                    <div>
                      <p className="mb-2">
                        <strong>Critical Assets (≤30 days):</strong> Immediate action required. Contact vendors for renewal quotes and process approvals.
                      </p>
                      <p className="mb-2">
                        <strong>Warning Assets (31-60 days):</strong> Begin renewal planning. Request quotes and evaluate replacement options.
                      </p>
                    </div>
                    <div>
                      <p className="mb-2">
                        <strong>Planning Assets (61-90 days):</strong> Schedule renewal reviews and budget allocation discussions.
                      </p>
                      <p className="mb-2">
                        <strong>Bulk Operations:</strong> Use keyboard shortcuts (Ctrl+R for renewals, Ctrl+A for select all) for efficient processing.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </main>
      <NotificationPanel 
        isOpen={showNotifications} 
        onClose={() => setShowNotifications(false)} 
      />
    </div>
  );
};

export default WarrantyManagementCenter;